import React, { useCallback, useRef, useState } from "react";
import { FileText, Upload, X } from "lucide-react";

/**
 * MultiFileDropzone — drag & drop + click to pick, no file count limit.
 *
 * Props:
 *   files: array of currently-attached files (base64 payload objects).
 *   onFilesAdded: (newFilesArray) => void  — called with newly-encoded files.
 *   onRemove: (index) => void              — remove existing file at index.
 *   label: optional heading text.
 *   accept: optional accept attr (defaults to any type).
 *   mode: "base64" (default) encodes each file to {name,size,mime,data_base64}
 *         "raw"   passes native File objects (for FormData uploads).
 *   testid: data-testid prefix.
 *   compact: if true, uses a thinner single-line dropzone.
 */
export default function MultiFileDropzone({
  files = [], onFilesAdded, onRemove,
  label = "Belgeleri sürükle-bırak veya tıklayarak seç (birden fazla seçebilirsiniz)",
  accept, mode = "base64", testid = "mfd", compact = false,
}) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const toB64 = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const b64 = String(fr.result).split(",")[1] || "";
      resolve({ name: file.name, size: file.size, mime: file.type || "application/octet-stream", data_base64: b64 });
    };
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });

  const handle = useCallback(async (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setBusy(true);
    try {
      if (mode === "raw") {
        onFilesAdded?.(arr);
      } else {
        const encoded = await Promise.all(arr.map(toB64));
        onFilesAdded?.(encoded);
      }
    } finally { setBusy(false); }
  }, [mode, onFilesAdded]);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setOver(false);
    handle(e.dataTransfer.files);
  };

  const onPick = (e) => {
    handle(e.target.files);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="w-full" data-testid={`${testid}-wrap`}>
      <div
        onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed transition flex items-center justify-center gap-2 px-3 ${
          compact ? "py-2" : "py-5"
        } ${over ? "border-indigo-500 bg-indigo-50/50" : "border-slate-300 bg-slate-50/40 hover:border-indigo-400 hover:bg-indigo-50/30"}`}
        data-testid={`${testid}-dropzone`}
      >
        <Upload size={compact ? 14 : 18} className={over ? "text-indigo-600" : "text-slate-500"}/>
        <div className="text-center">
          <div className={`${compact ? "text-[11px]" : "text-xs"} font-semibold text-slate-700`}>
            {busy ? "Dosyalar hazırlanıyor..." : label}
          </div>
          {!compact && (
            <div className="text-[10px] text-slate-400 mt-0.5">Fatura · Sertifika · Rapor birlikte yüklenebilir — adet/boyut sınırı yok</div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          onChange={onPick}
          className="hidden"
          data-testid={`${testid}-input`}
        />
      </div>
      {files.length > 0 && (
        <div className="mt-2 space-y-1" data-testid={`${testid}-list`}>
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1" data-testid={`${testid}-item-${i}`}>
              <FileText size={12} className="text-indigo-500 flex-shrink-0"/>
              <span className="flex-1 truncate" title={f.name}>{f.name}</span>
              <span className="text-slate-400 font-mono text-[10px]">{Math.round((f.size || 0) / 1024)} KB</span>
              {onRemove && (
                <button onClick={(e) => { e.stopPropagation(); onRemove(i); }} className="text-rose-600 hover:bg-rose-50 rounded p-0.5" data-testid={`${testid}-remove-${i}`} type="button">
                  <X size={12}/>
                </button>
              )}
            </div>
          ))}
          <div className="text-[10px] text-slate-500 font-mono text-right" data-testid={`${testid}-count`}>
            Toplam: {files.length} dosya · {Math.round(files.reduce((s, f) => s + (f.size || 0), 0) / 1024)} KB
          </div>
        </div>
      )}
    </div>
  );
}
